/**
 * ล็อกเป้า (the persistent lock-on, packages/shared systems/duelLock.ts): the client's side of it. Tab or a click on a foe
 * locks it (the `lockOn` command); Esc, a click on empty ground, or the target dying or going far lets it go (the rules do
 * that too). This reads which target is locked (the shared system when the rules run here, else the replica's target that
 * we locked), shows the lock marker (render/passes/lockMarker.ts) and the badge in the target frame, and tells the input
 * who is locked so A/D circle round it and W/S close in and back off (input.ts).
 */
import { duelLockOf, DUEL_LOCK, type PlayerCommand, type World } from '@nine/shared';
import { lockMark } from '../render/passes/lockMarker';

export class LockClient {
  /** The locked target's id on this replica, or -1. */
  id = -1;
  /** We asked for a lock and the rules are elsewhere (a server): the target is the lock until it goes. */
  private asked = false;
  private queued: number | null = null;
  /** Called when the lock changes (the input re-reads the keys held). */
  onChange: (() => void) | null = null;

  constructor(
    private readonly world: World,
    private readonly send: (c: PlayerCommand) => void,
  ) {}

  private get remote(): boolean {
    return !!(window as unknown as { __net?: unknown }).__net;
  }

  get active(): boolean {
    return this.id >= 0;
  }

  set(id: number): void {
    this.asked = true;
    this.send({ t: 'lockOn', op: 'set', id });
  }

  next(): void {
    this.asked = true;
    this.send({ t: 'lockOn', op: 'next' });
  }

  off(): void {
    this.asked = false;
    if (this.id >= 0 || this.remote) this.send({ t: 'lockOn', op: 'off' });
    this.update(-1);
  }

  private update(id: number): void {
    if (id !== this.id) {
      this.id = id;
      this.onChange?.();
    }
  }

  /** Per frame: who is locked, the marker, the frame's badge. */
  sync(dt: number): void {
    const w = this.world;
    const p = w.player;
    let id = -1;
    if (!this.remote) {
      id = duelLockOf(w)?.lockedOf(p.id) ?? -1;
      if (id < 0) this.asked = false;
    } else if (this.asked) {
      const t = w.get(p.targetId);
      if (t && !t.dead && Math.hypot(t.x - p.x, t.y - p.y) <= DUEL_LOCK.drop) id = t.id;
      else this.asked = false;
    }
    this.update(id);
    lockMark.id = id;
    lockMark.form = this.remote ? null : duelLockOf(w)?.formOf(p.id) ?? null;
    // The paired form's keys for both fighters (the renderer draws them over the hurt pose).
    lockMark.pose.clear();
    const sys = this.remote ? null : duelLockOf(w);
    if (sys && lockMark.form) for (const id of [lockMark.form[0], lockMark.form[1]]) {
      const fp = sys.poseOf(id);
      if (fp) lockMark.pose.set(id, fp);
    }
    lockMark.t += dt;
    // The move waiting to come into reach (the character is walking to the target): its slot glows.
    const q = this.remote ? null : duelLockOf(w)?.queuedOf(p.id) ?? null;
    if (q !== this.queued) {
      this.queued = q;
      for (const b of document.querySelectorAll<HTMLElement>('#hotbar button.slot.skill'))
        b.classList.toggle('queued', q !== null && b.querySelector('kbd.key')?.textContent === String(q + 1));
    }
    const badge = document.querySelector<HTMLElement>('#tlock');
    if (badge) badge.hidden = !(id >= 0 && p.targetId === id);
  }
}
